import * as THREE from 'three';

export const LEGACY_WORLD_LIGHT_SOURCE_POSITION = [-405.48682, 10_000, 6_504.1377] as const;

export const LEGACY_WORLD_LIGHT_DIRECTION = normalize(LEGACY_WORLD_LIGHT_SOURCE_POSITION);

/**
 * jME's fixed-function material defaults are ambient 0.2 and diffuse 0.8. Three's
 * Lambert BRDF divides both light contributions by PI and uses the diffuse color
 * for ambient light, so these factors preserve the original defaults.
 */
export const LEGACY_WORLD_AMBIENT_INTENSITY = Math.PI / 4;
export const LEGACY_WORLD_DIFFUSE_INTENSITY = Math.PI;
/** `phongshadows*.frag`: projected depth is sampled at `shadowCoord.b - 0.0065`. */
export const LEGACY_WORLD_SHADOW_BIAS = -0.0065;

const LEGACY_TO_BROWSER_WORLD_SCALE = 0.1;
const SHADOW_SOURCE_SCALE = 0.1 * LEGACY_TO_BROWSER_WORLD_SCALE;
const SHADOW_DEPTH_HALF_RANGE = 100 * LEGACY_TO_BROWSER_WORLD_SCALE;
const SHADOW_LOOK_AHEAD = 35 * LEGACY_TO_BROWSER_WORLD_SCALE;
const SHADOW_DECLARED_FOV_DEGREES = 7;
const SHADOW_SOURCE_OFFSET = new THREE.Vector3(
  ...LEGACY_WORLD_LIGHT_SOURCE_POSITION,
).multiplyScalar(SHADOW_SOURCE_SCALE);
const SHADOW_SOURCE_DISTANCE = SHADOW_SOURCE_OFFSET.length();
const shadowViewDirection = new THREE.Vector3();
const shadowCameraTarget = new THREE.Vector3();
const shadowFocus = new THREE.Vector3();

export type LegacyWorldLights = Readonly<{
  ambient: THREE.AmbientLight;
  source: THREE.DirectionalLight;
}>;

/**
 * The original uses an unattenuated PointLight more than 1,000 browser-world
 * units from the village. A directional source is visually equivalent across
 * the 12.8-unit village and avoids six point-light shadow passes.
 */
export function createLegacyWorldLights(
  ambientColor: readonly [number, number, number],
): LegacyWorldLights {
  const ambient = new THREE.AmbientLight(
    new THREE.Color(...ambientColor),
    LEGACY_WORLD_AMBIENT_INTENSITY,
  );
  ambient.name = 'world-ambient-light';

  const source = new THREE.DirectionalLight(0xffffff, LEGACY_WORLD_DIFFUSE_INTENSITY);
  source.position.copy(SHADOW_SOURCE_OFFSET);
  source.shadow.camera = new THREE.PerspectiveCamera(
    effectiveLegacyShadowFov(),
    1,
    SHADOW_SOURCE_DISTANCE - SHADOW_DEPTH_HALF_RANGE,
    SHADOW_SOURCE_DISTANCE + SHADOW_DEPTH_HALF_RANGE,
  ) as unknown as THREE.OrthographicCamera;
  source.shadow.bias = LEGACY_WORLD_SHADOW_BIAS;
  source.name = 'world-source-light';
  return { ambient, source };
}

/**
 * `abw.b(float)` centers the projected shadow map on the camera's ground target,
 * then moves it 35 source units farther along the horizontal view direction.
 * Its shadow camera sits at one tenth of the distant point-light location and
 * uses a narrow perspective frustum spanning 100 source units around that
 * distance. The browser world is one tenth of the source scale.
 */
export function updateLegacyWorldShadowProjection(
  source: THREE.DirectionalLight,
  camera: THREE.PerspectiveCamera,
  groundHeight: number,
): void {
  camera.getWorldDirection(shadowViewDirection);
  const groundDistance =
    Math.abs(shadowViewDirection.y) > Number.EPSILON
      ? (groundHeight - camera.position.y) / shadowViewDirection.y
      : 0;
  shadowCameraTarget.copy(camera.position).addScaledVector(shadowViewDirection, groundDistance);
  shadowFocus
    .copy(shadowViewDirection)
    .setY(0)
    .multiplyScalar(SHADOW_LOOK_AHEAD)
    .add(shadowCameraTarget);

  source.target.position.copy(shadowFocus);
  source.position.copy(shadowFocus).add(SHADOW_SOURCE_OFFSET);
  source.target.updateMatrixWorld();
  source.updateMatrixWorld();
}

function normalize(vector: readonly [number, number, number]): readonly [number, number, number] {
  const length = Math.hypot(...vector);
  return [vector[0] / length, vector[1] / length, vector[2] / length];
}

function effectiveLegacyShadowFov(): number {
  const declaredRadians = THREE.MathUtils.degToRad(SHADOW_DECLARED_FOV_DEGREES);
  return THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(declaredRadians) / 2));
}
